export type SourcePage = { source_revision_id: string; source_page_ref: string }
export const pageSourceKey = (page: SourcePage) => `${page.source_revision_id}:${page.source_page_ref}`
export const pageNumber = (ref: string) => Number(ref.split(':').at(-1)?.replace(/^p/i, '')) || 0

export type PageChange<T> = { type: 'insert' | 'delete' | 'move'; page: T; before: number | null; after: number | null }

/** Keep the longest unchanged sequence so insertions do not mark every following page as moved. */
export function compositionChanges<T extends SourcePage>(before: T[], after: T[]): PageChange<T>[] {
  const oldPositions = new Map(before.map((page, index) => [pageSourceKey(page), index + 1]))
  const newPositions = new Map(after.map((page, index) => [pageSourceKey(page), index + 1]))
  const retained = after.filter(page => oldPositions.has(pageSourceKey(page)))
  const tails: number[] = [], tailIndices: number[] = [], previous: number[] = []
  retained.forEach((page, index) => {
    const ordinal = oldPositions.get(pageSourceKey(page))!
    let low = 0, high = tails.length
    while (low < high) { const middle = (low + high) >>> 1; if (tails[middle]! < ordinal) low = middle + 1; else high = middle }
    previous[index] = low ? tailIndices[low - 1]! : -1
    tails[low] = ordinal
    tailIndices[low] = index
  })
  const stable = new Set<string>()
  for (let index = tailIndices.at(-1) ?? -1; index >= 0; index = previous[index]!) stable.add(pageSourceKey(retained[index]!))
  return [
    ...after.filter(page => !oldPositions.has(pageSourceKey(page))).map(page => ({ type: 'insert' as const, page, before: null, after: newPositions.get(pageSourceKey(page))! })),
    ...before.filter(page => !newPositions.has(pageSourceKey(page))).map(page => ({ type: 'delete' as const, page, before: oldPositions.get(pageSourceKey(page))!, after: null })),
    ...retained.filter(page => !stable.has(pageSourceKey(page))).map(page => ({ type: 'move' as const, page, before: oldPositions.get(pageSourceKey(page))!, after: newPositions.get(pageSourceKey(page))! })),
  ]
}

export function placePage<T extends SourcePage>(pages: T[], page: T, target: string | null, after = false): T[] {
  if (target === pageSourceKey(page)) return pages
  const output = pages.filter(item => pageSourceKey(item) !== pageSourceKey(page))
  const index = target ? output.findIndex(item => pageSourceKey(item) === target) : -1
  output.splice(index < 0 ? output.length : index + Number(after), 0, page)
  return output
}

/** Batch additions keep source order and never move pages already in the baseline. */
export function insertPages<T extends SourcePage>(pages: T[], incoming: T[], target: string | null, after = false): T[] {
  const seen = new Set(pages.map(pageSourceKey))
  const additions = incoming.filter(page => {
    const key = pageSourceKey(page)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  const index = target ? pages.findIndex(page => pageSourceKey(page) === target) : -1
  const offset = index < 0 ? pages.length : index + Number(after)
  return [...pages.slice(0, offset), ...additions, ...pages.slice(offset)]
}
